/**
 * Collection/PLP filtering, sorting and pagination — all state lives in the
 * URL (native Shopify filter/sort query params), never in a client-side
 * store. A change fetches the same URL with `section_id` appended (Section
 * Rendering API — same technique as assets/predictive-search.js), swaps this
 * element's contents with the fresh markup, and pushes the clean URL so the
 * page stays crawlable, bookmarkable and back-button-correct.
 *
 * One shared data/state layer serves two presentations: a persistent desktop
 * sidebar and a mobile/tablet bottom sheet. Only CSS decides which is
 * visible at a given width — both render from the same `collection.filters`
 * loop, so a filter change (fired from either one) updates both via the same
 * innerHTML swap.
 *
 * Listeners are bound once on this custom element itself (event delegation)
 * rather than on individual controls, so re-rendering the innerHTML after a
 * fetch never leaves stale/unbound elements behind.
 */
if (!customElements.get('collection-filters')) {
  customElements.define(
    'collection-filters',
    class CollectionFilters extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.abortController = null;
        this.lastTrigger = null;

        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('submit', (event) => event.preventDefault());
        this.bindSheet();

        // The custom sort menu isn't a native <dialog>, so closing it on an
        // outside click needs a document-level listener — a click inside
        // this element already reaches onClick via the bubbling listener
        // above, but a click anywhere else on the page (header, footer,
        // another section) never reaches this element at all.
        this.onDocumentClick = this.onDocumentClick.bind(this);
        document.addEventListener('click', this.onDocumentClick);

        window.addEventListener('popstate', () => window.location.reload());
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      // The native <dialog> "close" event doesn't bubble, so it can't be
      // caught by the delegated listeners above — bind it directly on the
      // dialog itself, and re-bind after every innerHTML swap since that
      // replaces the dialog node.
      bindSheet() {
        const sheet = this.querySelector('[data-filter-sheet]');
        sheet?.addEventListener('close', () => {
          this.lastTrigger?.focus();
        });
      }

      onChange(event) {
        const target = event.target;

        if (target.matches('[data-filter-checkbox]')) {
          const url = target.checked ? target.dataset.urlAdd : target.dataset.urlRemove;
          this.fetchAndRender(url);
        }
      }

      onClick(event) {
        const removeChip = event.target.closest('[data-remove-filter]');
        if (removeChip) {
          event.preventDefault();
          this.fetchAndRender(removeChip.href);
          return;
        }

        // Price range has no single value with its own url_to_remove (it's
        // two independent query params), so it's removed by dropping just
        // those two param names rather than following a pre-built link.
        const removePrice = event.target.closest('[data-remove-price]');
        if (removePrice) {
          event.preventDefault();
          const url = new URL(window.location.href);
          url.searchParams.delete(removePrice.dataset.minParam);
          url.searchParams.delete(removePrice.dataset.maxParam);
          url.searchParams.delete('page');
          this.fetchAndRender(url.toString());
          return;
        }

        const clearAll = event.target.closest('[data-clear-filters]');
        if (clearAll) {
          event.preventDefault();
          const url = new URL(window.location.href);
          Array.from(url.searchParams.keys()).forEach((key) => {
            if (key.startsWith('filter.')) url.searchParams.delete(key);
          });
          url.searchParams.delete('page');
          this.fetchAndRender(url.toString());
          return;
        }

        const sidebarToggle = event.target.closest('[data-toggle-sidebar]');
        if (sidebarToggle) {
          this.toggleSidebar();
          return;
        }

        const openSheetTrigger = event.target.closest('[data-open-sheet]');
        if (openSheetTrigger) {
          this.lastTrigger = openSheetTrigger;
          const sheet = this.querySelector('[data-filter-sheet]');
          if (sheet) {
            sheet.showModal();
            this.animateSheetOpen(sheet);
            sheet.querySelector('[data-close-filters]')?.focus();
          }
          return;
        }

        const closeTrigger = event.target.closest('[data-close-filters]');
        if (closeTrigger) {
          this.querySelector('[data-filter-sheet]')?.close();
          return;
        }

        const sheet = event.target.closest('[data-filter-sheet]');
        if (sheet && event.target === sheet) {
          sheet.close();
          return;
        }

        const priceApply = event.target.closest('[data-price-apply]');
        if (priceApply) {
          event.preventDefault();
          this.applyPriceRange(priceApply);
          return;
        }

        const sortTrigger = event.target.closest('[data-sort-trigger]');
        if (sortTrigger) {
          event.preventDefault();
          this.toggleSortMenu(sortTrigger);
          return;
        }

        const sortOption = event.target.closest('[data-sort-option]');
        if (sortOption) {
          event.preventDefault();
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortOption.dataset.value);
          this.closeSortMenu();
          this.fetchAndRender(url.toString());
        }
      }

      onKeydown(event) {
        const menu = this.querySelector('[data-sort-menu]');
        const trigger = this.querySelector('[data-sort-trigger]');
        if (!menu || !trigger || trigger.getAttribute('aria-expanded') !== 'true') return;

        const options = Array.from(menu.querySelectorAll('[data-sort-option]'));
        const currentIndex = options.indexOf(document.activeElement);

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeSortMenu();
          trigger.focus();
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          (options[currentIndex + 1] || options[0])?.focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          (options[currentIndex - 1] || options[options.length - 1])?.focus();
        } else if ((event.key === 'Enter' || event.key === ' ') && document.activeElement.matches('[data-sort-option]')) {
          event.preventDefault();
          document.activeElement.click();
        }
      }

      onDocumentClick(event) {
        const sortControl = this.querySelector('[data-sort-control]');
        const trigger = sortControl?.querySelector('[data-sort-trigger]');
        if (trigger?.getAttribute('aria-expanded') === 'true' && !sortControl.contains(event.target)) {
          this.closeSortMenu();
        }
      }

      toggleSortMenu(trigger) {
        const menu = this.querySelector('[data-sort-menu]');
        if (!menu) return;

        if (trigger.getAttribute('aria-expanded') === 'true') {
          this.closeSortMenu();
          return;
        }

        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
        (menu.querySelector('[aria-selected="true"]') || menu.querySelector('[data-sort-option]'))?.focus();
      }

      closeSortMenu() {
        const trigger = this.querySelector('[data-sort-trigger]');
        const menu = this.querySelector('[data-sort-menu]');
        trigger?.setAttribute('aria-expanded', 'false');
        if (menu) menu.hidden = true;
      }

      // Desktop-only, purely presentational — persists for the tab's
      // lifetime as a plain attribute on this element (untouched by the
      // innerHTML swaps below), which the section's CSS reads directly.
      toggleSidebar() {
        const collapsed = this.toggleAttribute('data-sidebar-collapsed');
        this.syncSidebarToggle(collapsed);
      }

      syncSidebarToggle(collapsed) {
        const trigger = this.querySelector('[data-toggle-sidebar]');
        if (!trigger) return;
        trigger.setAttribute('aria-expanded', String(!collapsed));
        const label = trigger.querySelector('[data-sidebar-toggle-label]');
        if (label) label.textContent = collapsed ? this.dataset.showFiltersLabel : this.dataset.hideFiltersLabel;
      }

      // Forces a style flush before adding the class that drives the
      // slide-up transform, so the browser always has a "before" frame to
      // transition from — needed because this <dialog> node is reused
      // across opens (a plain [open] CSS selector wouldn't reliably
      // re-animate on repeat opens of the same node). setTimeout rather
      // than requestAnimationFrame: rAF is paused entirely on a hidden/
      // backgrounded tab, which would leave the sheet stuck mid-transform
      // until the tab is refocused; a 0ms timeout still fires (just
      // throttled) either way.
      animateSheetOpen(sheet) {
        sheet.classList.remove('is-open');
        // eslint-disable-next-line no-unused-expressions
        sheet.offsetHeight;
        setTimeout(() => sheet.classList.add('is-open'), 0);
      }

      applyPriceRange(button) {
        const group = button.closest('[data-price-filter]');
        const minInput = group.querySelector('[data-price-min]');
        const maxInput = group.querySelector('[data-price-max]');

        const url = new URL(window.location.href);
        if (minInput.value) {
          url.searchParams.set(minInput.name, minInput.value);
        } else {
          url.searchParams.delete(minInput.name);
        }
        if (maxInput.value) {
          url.searchParams.set(maxInput.name, maxInput.value);
        } else {
          url.searchParams.delete(maxInput.name);
        }
        url.searchParams.delete('page');
        this.fetchAndRender(url.toString());
      }

      async fetchAndRender(url) {
        if (!url) return;

        const sheet = this.querySelector('[data-filter-sheet]');
        const wasOpen = !!sheet?.open;
        const sidebarCollapsed = this.hasAttribute('data-sidebar-collapsed');

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.toggleAttribute('aria-busy', true);

        try {
          const fetchUrl = new URL(url, window.location.origin);
          fetchUrl.searchParams.set('section_id', this.sectionId);

          const response = await fetch(fetchUrl.toString(), { signal: this.abortController.signal });
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newRoot = doc.querySelector('collection-filters');

          if (newRoot) {
            this.innerHTML = newRoot.innerHTML;
            this.bindSheet();
            this.syncSidebarToggle(sidebarCollapsed);
            if (wasOpen) {
              // Re-showing after a filter-triggered re-render — the sheet
              // was already visually open, so this should look instant
              // (no re-slide-in), unlike a genuine user-initiated open.
              const reopenedSheet = this.querySelector('[data-filter-sheet]');
              reopenedSheet?.showModal();
              reopenedSheet?.classList.add('is-open');
            }
          }

          const cleanUrl = new URL(url, window.location.origin);
          cleanUrl.searchParams.delete('section_id');
          window.history.pushState({}, '', `${cleanUrl.pathname}${cleanUrl.search}`);

          this.dispatchEvent(new CustomEvent('collection-filters:updated', { bubbles: true }));
        } catch (error) {
          if (error.name !== 'AbortError') {
            window.location.href = url;
          }
        } finally {
          this.toggleAttribute('aria-busy', false);
        }
      }
    }
  );
}
